import { useMemo } from "react";
import { Merchant } from "@/dto/merchant";
import {
  FEATURE_FLAG_PAZE_ON_COLO2,
  FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2,
  FEATURE_FLAG_QUANTUM_METRIC,
  FEATURE_FLAG_LOCALIZATION_ON_COLO2,
  DEPOSIT_TAKING_FOR_SERVICES_ENABLED,
  OLO_DISPLAY_SPECIAL_INSTRUCTIONS_FIELD_SETTINGS,
  FEATURE_FLAG_EARLY_ERROR_PREVENTION,
  FEATURE_FLAG_AU_SURCHARGE_WARNING,
  FEATURE_FLAG_COLOV3_MIGRATED,
  FEATURE_FLAG_COLOV3_RESTAURANT_MIGRATED,
  FEATURE_FLAG_COLOV3_RETAIL_MIGRATED,
  FEATURE_FLAG_COLOV3_SERVICES_MIGRATED,
  FEATURE_FLAG_COLOV3_CARTAPI_MIGRATED,
  FEATURE_FLAG_SHIPPING,
  FEATURE_FLAG_2026_COLO_SAVED_CARD_ON_FILE_ENABLED,
  FEATURE_FLAG_ADVANCED_ORDERING,
} from "@/utils/constants";
import { hasMerchantFeatureFlag } from "@/utils/merchantFeatureFlags";

export type FeatureFlagName =
  | typeof FEATURE_FLAG_PAZE_ON_COLO2
  | typeof FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2
  | typeof FEATURE_FLAG_QUANTUM_METRIC
  | typeof FEATURE_FLAG_LOCALIZATION_ON_COLO2
  | typeof DEPOSIT_TAKING_FOR_SERVICES_ENABLED
  | typeof OLO_DISPLAY_SPECIAL_INSTRUCTIONS_FIELD_SETTINGS
  // Bot-neutralization / early-error-prevention gate. Read from the merchant's
  // launchDarklyFeatures (see isEarlyErrorPreventionEnabled in
  // botNeutralization.ts). At the proxy layer no merchant is resolved yet, so that
  // layer instead honors the server-level EARLY_ERROR_PREVENTION_ENABLED env
  // mirror; this hook is for use wherever a merchant is available.
  | typeof FEATURE_FLAG_EARLY_ERROR_PREVENTION
  | typeof FEATURE_FLAG_AU_SURCHARGE_WARNING
  | typeof FEATURE_FLAG_COLOV3_MIGRATED
  | typeof FEATURE_FLAG_COLOV3_RESTAURANT_MIGRATED
  | typeof FEATURE_FLAG_COLOV3_RETAIL_MIGRATED
  | typeof FEATURE_FLAG_COLOV3_SERVICES_MIGRATED
  | typeof FEATURE_FLAG_COLOV3_CARTAPI_MIGRATED
  | typeof FEATURE_FLAG_SHIPPING
  | typeof FEATURE_FLAG_2026_COLO_SAVED_CARD_ON_FILE_ENABLED
  | typeof FEATURE_FLAG_ADVANCED_ORDERING;

/**
 * Checks if a specific LaunchDarkly feature flag is present and enabled for a merchant.
 * * @param merchant - The merchant object containing launchDarklyFeatures
 * @param flagName - The specific flag name to check
 * @returns boolean - True if the flag exists and is enabled
 */
export const useMerchantFeatureFlag = (
  merchant: Merchant | undefined | null,
  flagName: FeatureFlagName
): boolean => {
  return useMemo(
    () => hasMerchantFeatureFlag(merchant, flagName),
    [merchant, flagName]
  );
};
